import ArrowDropDown from "@mui/icons-material/ArrowDropDown";
import Check from "@mui/icons-material/Check";
import EditOff from "@mui/icons-material/EditOff";
import EditOutlined from "@mui/icons-material/EditOutlined";
import RateReview from "@mui/icons-material/RateReview";
import { Box, Chip, Menu, MenuItem, Skeleton, Tooltip } from "@mui/material";
import { alpha, type Theme } from "@mui/material/styles";
import { useCallback, useId, useState, type MouseEvent, type ReactElement } from "react";

import { SCOUT_WRITE_MODE_GQL, type ScoutWriteModeGql } from "../hooks/scoutWriteMode";
import {
  getScoutDataChangesIndicator,
  useScoutWriteAccessControl,
  type ScoutDataChangesChipColor,
} from "../hooks/useScoutWriteAccessControl";

/** Matches MUI `Chip` `size="small"` height. */
const WRITE_MODE_CHIP_HEIGHT_PX = 24;
/** Approximate width of full labels like "Ask before editing" + icon + caret. */
const WRITE_MODE_CHIP_WIDTH_PX = 148;
/** Approximate width of compact labels (Off / On / Ask) + icon + caret. */
const WRITE_MODE_CHIP_COMPACT_WIDTH_PX = 64;

export type ScoutWriteAccessIndicatorProps = {
  writeMode: ScoutWriteModeGql;
  onWriteModeChange: (next: ScoutWriteModeGql) => void;
  disabled?: boolean;
  hasScoutProAccess?: boolean;
  /**
   * Compact trigger labels (Off / On / Ask) for tight headers like the
   * Scout popover. Menu options stay full-length either way.
   */
  compact?: boolean;
  /** Show a chip-shaped skeleton while the conversation's stored mode loads. */
  loading?: boolean;
};

function chipPaletteColor(theme: Theme, color: ScoutDataChangesChipColor): string {
  if (color === "teal") {
    return theme.palette.encampGreen.dark;
  }
  if (color === "warning") {
    return theme.palette.warning.main;
  }
  return theme.palette.text.secondary;
}

function writeModeIcon(writeMode: ScoutWriteModeGql): ReactElement {
  if (writeMode === SCOUT_WRITE_MODE_GQL.AUTO) {
    return <EditOutlined />;
  }
  if (writeMode === SCOUT_WRITE_MODE_GQL.ASK) {
    return <RateReview />;
  }
  return <EditOff />;
}

function writeModeChipSx(theme: Theme, color: ScoutDataChangesChipColor, interactive: boolean) {
  const main = chipPaletteColor(theme, color);
  return {
    color: main,
    bgcolor: alpha(main, 0.08),
    ...(interactive
      ? {
          "&.MuiChip-clickable:hover": {
            bgcolor: alpha(main, 0.16),
          },
        }
      : {}),
    "& .MuiChip-icon": {
      color: "inherit",
      fontSize: 16,
      ml: 0.75,
    },
    "& .MuiChip-label": {
      px: 0.75,
    },
  };
}

export function ScoutWriteAccessIndicator({
  writeMode,
  onWriteModeChange,
  disabled = false,
  hasScoutProAccess = true,
  compact = false,
  loading = false,
}: ScoutWriteAccessIndicatorProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const menuId = useId();
  const {
    isFeatureEnabled,
    options,
    indicatorLabel,
    indicatorShortLabel,
    indicatorColor,
    indicatorTooltip,
    indicatorAriaLabel,
  } = useScoutWriteAccessControl({
    writeMode,
    disabled,
    hasScoutProAccess,
  });
  const triggerLabel = compact ? indicatorShortLabel : indicatorLabel;

  const handleOpen = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      if (disabled) {
        return;
      }
      setAnchorEl(event.currentTarget);
    },
    [disabled]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleSelect = useCallback(
    (next: ScoutWriteModeGql) => {
      setAnchorEl(null);
      if (disabled || next === writeMode) {
        return;
      }
      onWriteModeChange(next);
    },
    [disabled, onWriteModeChange, writeMode]
  );

  if (!isFeatureEnabled) {
    return null;
  }

  if (loading) {
    return (
      <Skeleton
        variant="rounded"
        width={compact ? WRITE_MODE_CHIP_COMPACT_WIDTH_PX : WRITE_MODE_CHIP_WIDTH_PX}
        height={WRITE_MODE_CHIP_HEIGHT_PX}
        aria-label="Loading data editing mode"
        sx={{ borderRadius: `${WRITE_MODE_CHIP_HEIGHT_PX / 2}px`, flexShrink: 0 }}
      />
    );
  }

  return (
    <>
      <Tooltip title={indicatorTooltip} arrow>
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
          <Chip
            icon={writeModeIcon(writeMode)}
            label={
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  lineHeight: 1,
                }}
              >
                {triggerLabel}
                <ArrowDropDown sx={{ fontSize: 16, width: 16, height: 16, mr: -0.5 }} />
              </Box>
            }
            size="small"
            clickable
            disabled={disabled}
            onClick={handleOpen}
            aria-label={indicatorAriaLabel}
            aria-haspopup="menu"
            aria-expanded={open ? "true" : undefined}
            aria-controls={open ? menuId : undefined}
            sx={(theme) => {
              const chipSx = writeModeChipSx(theme, indicatorColor, !disabled);
              return {
                flexShrink: 0,
                height: WRITE_MODE_CHIP_HEIGHT_PX,
                ...chipSx,
                // Keep mode colors when locked (e.g. turn in progress); dim instead of greying out.
                "&.Mui-disabled": {
                  opacity: 0.5,
                  color: chipSx.color,
                  bgcolor: chipSx.bgcolor,
                },
              };
            }}
          />
        </Box>
      </Tooltip>
      <Menu
        id={menuId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          list: {
            "aria-label": "Scout data changes",
            dense: true,
          },
          paper: {
            sx: { mt: 0.5 },
          },
        }}
      >
        {options.map((option) => {
          const optionIndicator = getScoutDataChangesIndicator(option.value);
          const isSelected = option.value === writeMode;
          return (
            <MenuItem
              key={option.value}
              selected={isSelected}
              aria-label={option.label}
              onClick={() => handleSelect(option.value)}
              sx={{ gap: 0.5, py: 0.75 }}
            >
              {isSelected ? <Check sx={{ fontSize: 16 }} /> : <Box sx={{ width: 16 }} />}
              <Chip
                icon={writeModeIcon(option.value)}
                label={optionIndicator.label}
                size="small"
                sx={(theme) => writeModeChipSx(theme, optionIndicator.color, false)}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
